import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PolicySection from './components/PolicySection';
import ContactInfo from './components/ContactInfo';
import TableOfContents from './components/TableOfContents';
import DataRightsCard from './components/DataRightsCard';
import Footer from '../../components/Footer';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>Privacy Policy | OpenbooQ</title>
        <meta name="description" content="Read OpenbooQ's Privacy Policy to understand how we collect, use, and protect your personal data on our transparent roofing platform." />
      </Helmet>
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-jakarta font-bold text-text-primary">Privacy Policy</h1>
                <p className="text-text-secondary mt-1">Transparent data practices you can trust</p>
              </div>
            </div>
            
            <p className="text-lg text-text-secondary mb-8">
              At OpenbooQ, transparency isn't just our business model—it's how we handle your data. 
              This policy explains exactly what information we collect, how we use it, and the control you have over your personal data.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Calendar" size={16} />
                <span>Last Updated: July 22, 2026</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Clock" size={16} />
                <span>Effective Date: November 2, 2025</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button variant="default" iconName="Download" iconPosition="left">
                Download PDF
              </Button>
              <Button variant="outline" iconName="Printer" iconPosition="left">
                Print Policy
              </Button>
              <Button variant="ghost" iconName="Share2" iconPosition="left">
                Share
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Table of Contents - Sidebar */}
            <div className="lg:col-span-1">
              <TableOfContents />
            </div>

            {/* Policy Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl p-8 lg:p-12 border border-border">
                
                {/* Information We Collect */}
                <PolicySection id="information-collection" title="Information We Collect">
                  <p className="text-text-secondary mb-6">
                    We collect information to provide better roofing services and maintain our commitment to transparency. Here's exactly what we collect and why:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-surface p-6 rounded-lg border border-border">
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                        <Icon name="User" size={20} className="text-primary mr-2" />
                        Personal Information
                      </h4>
                      <ul className="space-y-2 text-text-secondary">
                        <li>• Name, email address, and phone number</li>
                        <li>• Home address and property details</li>
                        <li>• Payment information (processed securely by Stripe)</li>
                        <li>• Communication preferences and history</li>
                      </ul>
                    </div>
                    
                    <div className="bg-surface p-6 rounded-lg border border-border">
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                        <Icon name="Activity" size={20} className="text-primary mr-2" />
                        Usage Information
                      </h4>
                      <ul className="space-y-2 text-text-secondary">
                        <li>• Website interactions and page views</li>
                        <li>• Roof measurement tool usage</li>
                        <li>• Material preferences and selections</li>
                        <li>• Support chat conversations</li>
                      </ul>
                    </div>
                    
                    <div className="bg-surface p-6 rounded-lg border border-border">
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                        <Icon name="Smartphone" size={20} className="text-primary mr-2" />
                        Technical Information
                      </h4>
                      <ul className="space-y-2 text-text-secondary">
                        <li>• IP address and device information</li>
                        <li>• Browser type and operating system</li>
                        <li>• Cookies and similar tracking technologies</li>
                        <li>• Location data (with your permission)</li>
                      </ul>
                    </div>
                  </div>
                </PolicySection>

                {/* How We Use Your Information */}
                <PolicySection id="information-use" title="How We Use Your Information">
                  <p className="text-text-secondary mb-6">
                    We use your information to deliver exceptional roofing services and maintain our transparent, customer-first approach:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="Home" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Service Delivery</h4>
                          <p className="text-sm text-text-secondary">Provide roofing prices, coordinate installations, and manage projects</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Icon name="MessageCircle" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Communication</h4>
                          <p className="text-sm text-text-secondary">Send project updates, support responses, and important notifications</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Icon name="CreditCard" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Payment Processing</h4>
                          <p className="text-sm text-text-secondary">Process payments securely and manage billing information</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="TrendingUp" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Service Improvement</h4>
                          <p className="text-sm text-text-secondary">Analyze usage patterns to enhance our platform and services</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Icon name="Shield" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Security & Fraud Prevention</h4>
                          <p className="text-sm text-text-secondary">Protect your account and prevent unauthorized access</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Icon name="Scale" size={20} className="text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-text-primary">Legal Compliance</h4>
                          <p className="text-sm text-text-secondary">Meet regulatory requirements and resolve disputes</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Information Sharing */}
                <PolicySection id="information-sharing" title="Information Sharing">
                  <p className="text-text-secondary mb-6">
                    We share your information only when necessary to provide our services or as required by law. Here's our complete sharing policy:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                        <Icon name="CheckCircle" size={20} className="text-green-600 mr-2" />
                        We DO Share With:
                      </h4>
                      <ul className="space-y-2 text-green-700">
                        <li>• <strong>Certified Contractors:</strong> Project details for service delivery</li>
                        <li>• <strong>Payment Processors:</strong> Billing information for secure transactions</li>
                        <li>• <strong>Service Providers:</strong> Analytics and support tools (anonymized when possible)</li>
                        <li>• <strong>Legal Authorities:</strong> When required by law or to protect rights</li>
                      </ul>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                        <Icon name="XCircle" size={20} className="text-red-600 mr-2" />
                        We NEVER Share With:
                      </h4>
                      <ul className="space-y-2 text-red-700">
                        <li>• Marketing companies or data brokers</li>
                        <li>• Unrelated third parties for their marketing</li>
                        <li>• Social media platforms (unless you connect accounts)</li>
                        <li>• Anyone for profit or without your explicit consent</li>
                      </ul>
                    </div>
                  </div>
                </PolicySection>

                {/* Data Security */}
                <PolicySection id="data-security" title="Data Security">
                  <p className="text-text-secondary mb-6">
                    We implement industry-leading security measures to protect your personal information:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-surface rounded-lg border border-border">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="Lock" size={24} className="text-primary" />
                      </div>
                      <h4 className="font-semibold text-text-primary mb-2">Encryption</h4>
                      <p className="text-sm text-text-secondary">All data encrypted in transit and at rest using AES-256 encryption</p>
                    </div>
                    
                    <div className="text-center p-6 bg-surface rounded-lg border border-border">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="Server" size={24} className="text-primary" />
                      </div>
                      <h4 className="font-semibold text-text-primary mb-2">Secure Infrastructure</h4>
                      <p className="text-sm text-text-secondary">SOC 2 compliant hosting with regular security audits and monitoring</p>
                    </div>
                    
                    <div className="text-center p-6 bg-surface rounded-lg border border-border">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="UserCheck" size={24} className="text-primary" />
                      </div>
                      <h4 className="font-semibold text-text-primary mb-2">Access Controls</h4>
                      <p className="text-sm text-text-secondary">Multi-factor authentication and role-based access for all team members</p>
                    </div>
                  </div>
                </PolicySection>

                {/* Cookies & Tracking */}
                <PolicySection id="cookies-tracking" title="Cookies & Tracking Technologies">
                  <p className="text-text-secondary mb-4">
                    We use cookies and similar technologies both to run the site and to measure our
                    advertising. They fall into two groups:
                  </p>
                  <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                    <li><span className="font-medium text-text-primary">Essential &amp; analytics</span> — needed to operate the site and understand how it&apos;s used, including first-party session analytics.</li>
                    <li><span className="font-medium text-text-primary">Advertising &amp; conversion</span> — help us see which marketing brings homeowners to us (our connected-TV conversion pixel and the Google and Meta advertising tags loaded through our tag manager).</li>
                  </ul>
                  <p className="text-text-secondary mb-2">
                    <span className="font-medium text-text-primary">Your choice — no cookie wall.</span> You can opt out of
                    advertising and conversion tracking at any time on our{' '}
                    <Link to="/privacy-choices" className="text-primary font-semibold hover:underline">Your Privacy Choices</Link>{' '}
                    page. We also automatically honor the <span className="font-medium text-text-primary">Global Privacy Control (GPC)</span>{' '}
                    browser signal as an opt-out — if your browser or a privacy extension sends it, we treat you as opted out with no action needed.
                  </p>
                </PolicySection>

                {/* User Rights */}
                <PolicySection id="user-rights" title="Your Rights & Choices">
                  <p className="text-text-secondary mb-6">
                    You have comprehensive control over your personal information. Exercise your rights easily through our self-service tools:
                  </p>
                  
                  <DataRightsCard />
                </PolicySection>

                {/* Data Retention */}
                <PolicySection id="data-retention" title="Data Retention">
                  <p className="text-text-secondary mb-6">
                    We retain your information only as long as necessary to provide services and meet legal obligations:
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border border-border rounded-lg">
                      <thead className="bg-surface">
                        <tr>
                          <th className="text-left p-4 font-semibold text-text-primary border-b border-border">Data Type</th>
                          <th className="text-left p-4 font-semibold text-text-primary border-b border-border">Retention Period</th>
                          <th className="text-left p-4 font-semibold text-text-primary border-b border-border">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border">
                          <td className="p-4 text-text-primary">Account Information</td>
                          <td className="p-4 text-text-secondary">Until account deletion</td>
                          <td className="p-4 text-text-secondary">Service provision</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="p-4 text-text-primary">Project Records</td>
                          <td className="p-4 text-text-secondary">7 years after completion</td>
                          <td className="p-4 text-text-secondary">Warranty support, legal compliance</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="p-4 text-text-primary">Payment Information</td>
                          <td className="p-4 text-text-secondary">7 years</td>
                          <td className="p-4 text-text-secondary">Tax and accounting requirements</td>
                        </tr>
                        <tr className="border-b border-border">
                          <td className="p-4 text-text-primary">Marketing Data</td>
                          <td className="p-4 text-text-secondary">Until opt-out</td>
                          <td className="p-4 text-text-secondary">Communication preferences</td>
                        </tr>
                        <tr>
                          <td className="p-4 text-text-primary">Analytics Data</td>
                          <td className="p-4 text-text-secondary">26 months</td>
                          <td className="p-4 text-text-secondary">Service improvement</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </PolicySection>

                {/* Third-Party Services */}
                <PolicySection id="third-party-services" title="Third-Party Services">
                  <p className="text-text-secondary mb-6">
                    We work with trusted partners to provide our services. Here are the third-party services we use and their privacy practices:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CreditCard" size={20} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">Stripe (Payment Processing)</h4>
                        <p className="text-sm text-text-secondary mb-2">Handles all payment transactions securely. PCI DSS Level 1 certified.</p>
                        <Link to="https://stripe.com/privacy" className="text-sm text-primary hover:underline">View Stripe Privacy Policy</Link>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="BarChart3" size={20} className="text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">Google — Analytics (GA4), Ads &amp; Tag Manager</h4>
                        <p className="text-sm text-text-secondary mb-2">Website analytics plus advertising and conversion measurement, loaded via Google Tag Manager. You can opt out of the advertising use on Your Privacy Choices.</p>
                        <Link to="https://policies.google.com/privacy" className="text-sm text-primary hover:underline">View Google Privacy Policy</Link>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Facebook" size={20} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">Meta (Facebook / Instagram Ads)</h4>
                        <p className="text-sm text-text-secondary mb-2">The Meta advertising pixel (loaded via our tag manager) measures ad conversions and builds ad audiences. You can opt out on Your Privacy Choices.</p>
                        <Link to="https://www.facebook.com/privacy/policy" className="text-sm text-primary hover:underline">View Meta Privacy Policy</Link>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Tv" size={20} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">VisionBridge (Connected-TV Ad Measurement)</h4>
                        <p className="text-sm text-text-secondary mb-2">Measures conversions from our connected-TV (streaming) advertising. You can opt out on Your Privacy Choices.</p>
                        <Link to="https://www.visionbridge.tv/privacy" className="text-sm text-primary hover:underline">View VisionBridge Privacy Policy</Link>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Activity" size={20} className="text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">Microsoft Clarity (Product Analytics)</h4>
                        <p className="text-sm text-text-secondary mb-2">First-party session analytics and heatmaps that help us improve the site experience.</p>
                        <Link to="https://privacy.microsoft.com/privacystatement" className="text-sm text-primary hover:underline">View Microsoft Privacy Statement</Link>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="ShieldCheck" size={20} className="text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">TrustedForm (Consent Certification)</h4>
                        <p className="text-sm text-text-secondary mb-2">Documents your consent when you submit a form so we can verify TCPA/SMS consent. Provided by ActiveProspect.</p>
                        <Link to="https://activeprospect.com/privacy-policy/" className="text-sm text-primary hover:underline">View ActiveProspect Privacy Policy</Link>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4 p-4 bg-surface rounded-lg border border-border">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="MessageSquare" size={20} className="text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary mb-1">Chatbase (Chat Support)</h4>
                        <p className="text-sm text-text-secondary mb-2">Powers our website chat assistant.</p>
                        <Link to="https://www.chatbase.co/legal/privacy" className="text-sm text-primary hover:underline">View Chatbase Privacy Policy</Link>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Children's Privacy */}
                <PolicySection id="children-privacy" title="Children's Privacy">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                      <Icon name="AlertTriangle" size={20} className="text-amber-600 mt-1" />
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-2">Age Restriction</h4>
                        <p className="text-amber-700 mb-4">
                          Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.
                        </p>
                        <p className="text-amber-700">
                          If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at privacy@openbooq.com, and we will delete such information from our records.
                        </p>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Policy Changes */}
                <PolicySection id="policy-changes" title="Policy Changes">
                  <p className="text-text-secondary mb-6">
                    We may update this privacy policy from time to time. Here's how we handle changes:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Bell" size={20} className="text-primary mt-1" />
                      <div>
                        <h4 className="font-medium text-text-primary mb-1">Notification Process</h4>
                        <p className="text-text-secondary">We'll notify you of significant changes via email and prominent website notice at least 30 days before they take effect.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Icon name="GitBranch" size={20} className="text-primary mt-1" />
                      <div>
                        <h4 className="font-medium text-text-primary mb-1">Version History</h4>
                        <p className="text-text-secondary">Previous versions of this policy are archived and available upon request for transparency.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Icon name="UserCheck" size={20} className="text-primary mt-1" />
                      <div>
                        <h4 className="font-medium text-text-primary mb-1">Your Consent</h4>
                        <p className="text-text-secondary">Continued use of our services after policy changes constitutes acceptance of the updated terms.</p>
                      </div>
                    </div>
                  </div>
                </PolicySection>

                {/* Contact Us */}
                <PolicySection id="contact-us" title="Contact Us">
                  <p className="text-text-secondary mb-6">
                    Have questions about this privacy policy or how we handle your data? We're here to help with complete transparency.
                  </p>
                  
                  <ContactInfo />
                </PolicySection>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Body Copy Section */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Data Privacy</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            At OpenbooQ, we believe that transparency in pricing should extend to transparency in data practices. When you use our platform to get a roof replacement price, we collect only the information necessary to provide you with an accurate quote and complete your installation directly. We never sell your personal information to third parties, and we never share your contact details without your explicit consent.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            The data we collect — including your address, roof measurements, and contact information — is used solely to generate your roof price, match you with qualified installers in your service area, and communicate with you about your roofing project. We use industry-standard encryption and security protocols to protect your information at every step of the process.
          </p>
          <p className="text-gray-600 leading-relaxed">
            You have the right to access, correct, or delete any personal information we hold about you at any time. If you have questions about how we handle your data, or if you'd like to exercise your privacy rights, please contact our support team at the information provided in this policy. We are committed to responding to all privacy-related inquiries within 30 days.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;